import axios from "axios";
import { EarningsDate } from "../models/EarningsDate";
import { setNotification } from "../../store/notifications-reducer";
import { v4 } from "uuid";
import { EarningsReportV2 } from "../models/EarningsReportV2";
import type { ShareAuthOptions } from "./conversations";

const composeAuthHeaders = ({ accessToken, shareLinkToken }: ShareAuthOptions): Record<string, string> => {
    const headers: Record<string, string> = {
        'X-Request-ID': v4()
    };

    if (accessToken) {
        headers['Authorization'] = accessToken;
    }

    if (shareLinkToken) {
        headers['X-PQ-Share-Link'] = shareLinkToken;
    }

    return headers;
};

// Latest Events types
export interface LatestEvent {
    id: string;
    ticker: string;
    companyName: string;
    eventType: 'earnings_call' | '10-k' | '10-q' | '8-k';
    quarter: string;
    fiscalDateEnding: string;
    isTranscript: boolean;
}

export interface LatestEventsResponse {
    events: LatestEvent[];
    total: number;
}

// Async function to get the latest events for splash page (no auth required)
export const getLatestEvents = async (
    limit?: number, 
    offset?: number, 
    eventType?: string, 
    search?: string
): Promise<LatestEventsResponse> => {
    const params = new URLSearchParams();
    
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    if (eventType && eventType !== 'all') params.append('eventType', eventType);
    if (search && search.trim() !== '') params.append('search', search.trim());
    
    const queryString = params.toString();
    const url = `${process.env.REACT_APP_API_URL}/v1/latest-events${queryString ? `?${queryString}` : ''}`;
    
    const response = await axios.get(url);

    if (response.status !== 200) {
        throw new Error('Failed to get latest events');
    }

    return response.data as LatestEventsResponse;
};

// Async function to get the earnings report by fiscal period and year
export const getEarningsReport = async (
    ticker: string,
    fiscalPeriod: string,
    fiscalYear: string,
    auth: ShareAuthOptions,
    dispatch: (action: any) => void
): Promise<EarningsReportV2> => {
    const response = await axios.get(process.env.REACT_APP_API_URL + `/v1/stocks/${ticker}/earnings/${fiscalPeriod}/${fiscalYear}`, {
        headers: composeAuthHeaders(auth)
    });

    if (response.status !== 200) {
        if (response.status === 403 && response?.data?.code === 1) {
            dispatch(setNotification({
                message: 'You do not have access to this earnings report. Please upgrade to premium to access all earnings.',
                type: 'error'
            }));
        }
        throw new Error('Failed to get earnings report');
    }

    return response.data as EarningsReportV2;
};

// Async function to get the earnings dates
export const getEarningsDates = async (
    { ticker, entityType }: { ticker: string, entityType: 'transcript' | 'earnings' },
    auth: ShareAuthOptions
): Promise<{ entityType: 'transcript' | 'earnings', dates: EarningsDate[] }> => {
    const response = await axios.get(process.env.REACT_APP_API_URL + `/v1/stocks/${ticker}/earnings_dates?entityType=${entityType}`, {
        headers: composeAuthHeaders(auth)
    });

    if (response.status !== 200) {
        throw new Error('Failed to get earnings dates');
    }

    return {
        entityType: entityType,
        dates: response.data as EarningsDate[]
    };
};
